import { useCallback, useEffect, useRef, useState } from "react";
import { TVerificationRequestAccess } from "../models";
import { getVerificationRequestAccess } from "../network";

const READONLY_ACCESS: TVerificationRequestAccess = { mode: "readonly" };

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error("Unknown error");
}

export const useVerificationRequestAccess = (
  verificationRequestId?: string,
): [TVerificationRequestAccess, boolean, Error | null, () => Promise<void>] => {
  const [access, setAccess] =
    useState<TVerificationRequestAccess>(READONLY_ACCESS);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const requestSeq = useRef(0);

  const request = useCallback(async () => {
    const currentRequest = requestSeq.current + 1;
    requestSeq.current = currentRequest;
    setError(null);

    if (!verificationRequestId) {
      setAccess(READONLY_ACCESS);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getVerificationRequestAccess(
        verificationRequestId,
      );
      if (requestSeq.current === currentRequest) {
        setAccess(response);
      }
    } catch (e) {
      if (requestSeq.current === currentRequest) {
        setAccess(READONLY_ACCESS);
        setError(toError(e));
      }
    } finally {
      if (requestSeq.current === currentRequest) {
        setIsLoading(false);
      }
    }
  }, [verificationRequestId]);

  useEffect(() => {
    request();

    return () => {
      requestSeq.current += 1;
    };
  }, [request]);

  return [access, isLoading, error, request];
};
